package com.sddamico.models

import androidx.compose.runtime.Immutable
import kotlinx.serialization.Serializable

// Auth
@Serializable
data class MeResponse(
    val id: String,
    val fullName: String,
    val email: String,
    val role: String,
    val clubId: String? = null,
    val clubName: String? = null,
    val emailRosterChanges: Boolean = true,
    val emailWeekendRecap: Boolean = true,
    val emailWaiverReports: Boolean = true,
    val pushRosterChanges: Boolean = true,
    val pushTournamentStart: Boolean = true,
    val pushTournamentComplete: Boolean = true,
    val emailPickTrades: Boolean = true,
    val pushPickTrades: Boolean = true,
    val pushMadeTheCut: Boolean = true,
    val pushTennisAdvancement: Boolean = true,
    val isImpersonating: Boolean = false,
    val realUserName: String? = null,
    val realUserRole: String? = null,
)

@Serializable
data class ImpersonateRequest(val clubId: String)

@Serializable
data class SendOtpRequest(val email: String)

@Serializable
data class VerifyRequest(val email: String, val code: String)

// League Year
@Serializable
data class LeagueYearDetailResponse(
    val id: String,
    val year: Int,
    val teams: List<TeamRefResponse>,
    val tournaments: List<TournamentRefResponse>,
    val standings: List<TeamStandingResponse>,
    val lastSyncedAt: String? = null,
)

@Serializable
data class TeamRefResponse(val id: String, val name: String)

@Serializable
data class TournamentRefResponse(
    val id: String,
    val name: String,
    val sport: String,
    val importance: String,
    val startDate: String,
    val endDate: String? = null,
    val hasProvisionalResults: Boolean = false,
    val status: String,
    val topResults: List<TournamentTopResult>? = null,
    val upcomingMatches: List<UpcomingMatchResponse>? = null,
    val espnUrl: String? = null,
    val location: String? = null,
    val isScored: Boolean = true,
    val purse: Long? = null,
)

@Serializable
data class UpcomingMatchResponse(
    val player1Name: String,
    val player1ClubName: String,
    val player2Name: String? = null,
    val player2ClubName: String? = null,
    val round: String,
    val scheduledTime: String? = null,
    val state: String = "pre",
)

@Serializable
data class TournamentTopResult(
    val playerName: String,
    val clubName: String,
    val standing: Int,
    val points: Double,
    val provisional: Boolean,
)

@Serializable
data class TeamStandingResponse(
    val clubId: String,
    val clubName: String,
    val golfPoints: Double,
    val golfRank: Int,
    val mensTennisPoints: Double,
    val mensTennisRank: Int,
    val womensTennisPoints: Double,
    val womensTennisRank: Int,
    val overallScore: Double,
    val provisionalGolfPoints: Double = 0.0,
    val provisionalMensTennisPoints: Double = 0.0,
    val provisionalWomensTennisPoints: Double = 0.0,
)

// Club Roster
@Serializable
data class ClubRosterResponse(
    val clubId: String,
    val clubName: String,
    val ownerName: String = "",
    val totalPoints: Double,
    val provisionalPoints: Double = 0.0,
    val overallRank: Int,
    val sportRanks: Map<String, Int>,
    val sportPoints: Map<String, Double> = emptyMap(),
    val players: List<RosterPlayerResponse>,
    val formerPlayers: List<FormerPlayerResponse> = emptyList(),
    val draftPicks: List<DraftPickResponse> = emptyList(),
)

@Serializable
data class DraftPickResponse(
    val id: String,
    val year: Int,
    val round: String,
    val originalOwnerName: String,
    val isTraded: Boolean,
)

@Serializable
data class FormerPlayerResponse(
    val id: String,
    val name: String,
    val sport: String,
    val seasonPoints: Double,
)

@Serializable
data class RosterPlayerResponse(
    val id: String,
    val name: String,
    val sport: String,
    val rosterSlot: String,
    val age: Int? = null,
    val birthDate: String? = null,
    val seasonPoints: Double,
    val provisionalPoints: Double = 0.0,
    val activeTournament: ActiveTournamentInfo? = null,
    val isLiv: Boolean = false,
    val rankingLabel: String? = null,
    val rankingValue: Int? = null,
)

@Serializable
data class ActiveTournamentInfo(
    val tournamentName: String,
    val standing: String,
    val provisionalPoints: Double,
)

// Roster Moves
@Serializable
data class ExecuteRosterMoveRequest(
    val moveType: String,
    val clubId: String,
    val rosterSlot: String,
    val addedPlayerId: String? = null,
    val newPlayerName: String? = null,
    val effectiveDate: String? = null,
    val notes: String? = null,
    val toLivLine: Boolean = false,
)

@Serializable
data class RosterMoveResponse(
    val id: String,
    val moveType: String,
    val clubName: String,
    val addedPlayerName: String? = null,
    val droppedPlayerName: String? = null,
    val rosterSlot: String,
    val effectiveDate: String,
    val performedBy: String,
    val createdAt: String,
    val notes: String? = null,
    val warning: String? = null,
)

@Serializable
data class RosterMoveListResponse(val moves: List<RosterMoveResponse>)

// Players
@Immutable
@Serializable
data class ClubRefResponse(val id: String, val name: String)

@Serializable
data class PlayerSummaryResponse(
    val id: String,
    val name: String,
    val sport: String,
    val currentClub: ClubRefResponse?,
    val rosterSlot: String?,
)

@Serializable
data class PlayerListResponse(val players: List<PlayerSummaryResponse>)

// Clubs
@Serializable
data class ClubResponse(val id: String, val name: String)

@Serializable
data class ClubListResponse(val clubs: List<ClubResponse>)

// Manage Clubs (LM admin)
@Serializable
data class OwnerResponse(
    val id: String,
    val name: String,
    val email: String,
)

@Serializable
data class ManagedClubResponse(
    val clubId: String,
    val clubName: String,
    val owners: List<OwnerResponse>,
)

@Serializable
data class ManagedClubListResponse(val clubs: List<ManagedClubResponse>)

@Serializable
data class UpdateClubRequest(
    val clubId: String,
    val clubName: String? = null,
    val ownerId: String? = null,
    val ownerName: String? = null,
    val ownerEmail: String? = null,
)

@Serializable
data class AddOwnerRequest(
    val clubId: String,
    val ownerName: String,
    val ownerEmail: String,
)

@Serializable
data class DeleteOwnerRequest(
    val clubId: String,
    val ownerId: String,
)

// Player Universe
@Immutable
@Serializable
data class PlayerUniverseEntry(
    val id: String,
    val name: String,
    val sport: String,
    val age: Int? = null,
    val birthDate: String? = null,
    val currentClub: ClubRefResponse? = null,
    val rosterSlot: String? = null,
    val seasonPoints: Double,
    val waiverEndDate: String? = null,
    val isLiv: Boolean = false,
    val rankingLabel: String? = null,
    val rankingValue: Int? = null,
)

@Serializable
data class PlayerUniverseResponse(
    val players: List<PlayerUniverseEntry>,
    val leagueYearId: String,
)

// Claims
@Serializable
data class SubmitClaimRequest(
    val addedPlayerId: String,
    val droppedPlayerId: String? = null,
    val notes: String? = null,
)

@Serializable
data class ResolveClaimRequest(
    val claimId: String,
    val action: String,
)

@Serializable
data class CancelClaimRequest(
    val claimId: String,
)

@Serializable
data class PlayerClaimResponse(
    val id: String,
    val clubName: String,
    val claimedByName: String,
    val addedPlayerName: String,
    val addedPlayerId: String,
    val addedPlayerSport: String,
    val droppedPlayerName: String? = null,
    val droppedPlayerId: String? = null,
    val rosterSlot: String,
    val status: String,
    val claimType: String? = null,
    val waiverEndDate: String? = null,
    val createdAt: String,
    val resolvedByName: String? = null,
    val resolvedAt: String? = null,
    val notes: String? = null,
    val duplicateClaimCount: Int = 0,
)

@Serializable
data class PlayerClaimListResponse(val claims: List<PlayerClaimResponse>)

// Settings
@Serializable
data class UpdateSettingsRequest(
    val fullName: String? = null,
    val clubName: String? = null,
    val emailRosterChanges: Boolean? = null,
    val emailWeekendRecap: Boolean? = null,
    val emailWaiverReports: Boolean? = null,
    val pushRosterChanges: Boolean? = null,
    val pushTournamentStart: Boolean? = null,
    val pushTournamentComplete: Boolean? = null,
    val emailPickTrades: Boolean? = null,
    val pushPickTrades: Boolean? = null,
    val pushMadeTheCut: Boolean? = null,
    val pushTennisAdvancement: Boolean? = null,
)

@Serializable
data class ChangeEmailRequest(val newEmail: String)

@Serializable
data class VerifyEmailChangeRequest(val newEmail: String, val code: String)

// Tournament Updates
@Serializable
data class UpdateTournamentsRequest(
    val tournaments: List<TournamentUpdate>,
)

@Serializable
data class TournamentUpdate(
    val id: String,
    val isScored: Boolean,
    val importance: String,
)

// Push Notifications
@Serializable
data class VapidKeyResponse(val publicKey: String)

@Serializable
data class PushSubscribeRequest(
    val endpoint: String,
    val p256dh: String,
    val auth: String,
)

@Serializable
data class PushUnsubscribeRequest(val endpoint: String)

// Player Scoring
@Serializable
data class PlayerScoringResponse(
    val playerId: String,
    val playerName: String,
    val entries: List<ScoringTournamentEntry>,
)

@Serializable
data class ScoringTournamentEntry(
    val tournamentName: String,
    val importance: String,
    val standing: String,
    val points: Double,
    val provisional: Boolean,
)

// Draft Pick Trades
@Serializable
data class ExecuteDraftPickTradeRequest(
    val clubAId: String,
    val clubBId: String,
    val clubAPickIds: List<String>,
    val clubBPickIds: List<String> = emptyList(),
    val effectiveDate: String? = null,
    val notes: String? = null,
)

@Serializable
data class DraftPickTradeResponse(
    val id: String,
    val clubAName: String,
    val clubBName: String,
    val clubAPicksGiven: List<TradedPickInfo>,
    val clubBPicksGiven: List<TradedPickInfo>,
    val effectiveDate: String,
    val performedBy: String,
    val createdAt: String,
    val notes: String? = null,
)

@Serializable
data class TradedPickInfo(
    val year: Int,
    val round: String,
    val originalOwnerName: String,
)

@Serializable
data class ClubPicksResponse(val picks: List<DraftPickResponse>)

// Unified Transactions
@Serializable
data class TransactionResponse(
    val type: String,
    val createdAt: String,
    val rosterMove: RosterMoveResponse? = null,
    val pickTrade: DraftPickTradeResponse? = null,
    val tradeOffer: TradeOfferResponse? = null,
)

@Serializable
data class TransactionListResponse(val transactions: List<TransactionResponse>)

// Trades
@Serializable
data class TradeAssetRequest(
    val playerId: String? = null,
    val draftPickId: String? = null,
)

@Serializable
data class CreateTradeOfferRequest(
    val receiverClubId: String,
    val senderAssets: List<TradeAssetRequest>,
    val receiverAssets: List<TradeAssetRequest>,
    val notes: String? = null,
)

@Serializable
data class TradeOfferAssetResponse(
    val id: String,
    val fromClubId: String,
    val fromClubName: String,
    val playerId: String? = null,
    val playerName: String? = null,
    val playerSport: String? = null,
    val draftPickId: String? = null,
    val draftPickLabel: String? = null,
)

@Serializable
data class TradeOfferResponse(
    val id: String,
    val senderClubId: String,
    val senderClubName: String,
    val receiverClubId: String,
    val receiverClubName: String,
    val createdByName: String,
    val status: String,
    val assets: List<TradeOfferAssetResponse>,
    val createdAt: String,
    val resolvedAt: String? = null,
    val notes: String? = null,
)

@Serializable
data class TradeOfferListResponse(val trades: List<TradeOfferResponse>)

@Serializable
data class AcceptTradeOfferRequest(val tradeOfferId: String, val droppedPlayerIds: List<String> = emptyList())

@Serializable
data class DeclineTradeOfferRequest(val tradeOfferId: String)

@Serializable
data class CancelTradeOfferRequest(val tradeOfferId: String)
